import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { CampaignTriggerType } from '@dev/ee-api/ee-api-models';
import { Alert, DayPicker, TimeInput, Typography } from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';
import styled from 'styled-components';

import dayjs from '@/services/tools/dayjs';

import { LONG_DATE_FORMAT } from '@/services/constants';
import { ModifyCampaignMap } from '@/store/campaignCreator/actions';
import { UpdateDialog } from '@/store/dialog/actions';
import { useCampaignScreensPathsChecker } from '../../hooks/useCampaignScreensPathsChecker';
import { useGetScheduledInfo } from '../../hooks/useGetScheduleInfo';
import { TimeInputProps } from './TimeInputSectionInterface';

const TimePickersWrapper = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
    gap: 12px;
    margin-bottom: 32px;

    .inputWrapperBasicInput {
        flex: none;
        input[type='time']::-webkit-calendar-picker-indicator {
            display: none;
        }
    }
`;

const AlertWrapper = styled.div`
    margin-bottom: 16px;
`;

export const TimeInputSection = ({ type }: TimeInputProps): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { isDetailsScreen } = useCampaignScreensPathsChecker();
    const { sendEveryXTimes } = useGetScheduledInfo();
    const [isScheduleError, setScheduleError] = useState(false);
    const [isTimeInputError, setIsTimeInputError] = useState(false);
    const [inputsDate, setInputsDate] = useState(sendEveryXTimes);
    const isTime12Format = !!convertToLocalTime(sendEveryXTimes.toString()).match(/am|pm/i);

    const onChange = (newDate): void => {
        if (dayjs(newDate).format() === inputsDate.format() && !isTimeInputError) return;

        setInputsDate(dayjs(newDate));

        const isError = dayjs(newDate)?.isSameOrBefore?.(dayjs());
        setScheduleError(isError);

        if (isError) {
            dispatch(UpdateDialog({ isDisabled: true }));
        } else {
            dispatch(UpdateDialog({ isDisabled: false }));

            const modifyMap = new Map<string, any>();
            modifyMap.set('TriggerOptions.DateToSendOn', dayjs(newDate).utc().format(LONG_DATE_FORMAT));
            modifyMap.set('TriggerOptions.Type', CampaignTriggerType.FutureScheduled);

            dispatch(ModifyCampaignMap(modifyMap));
        }
    };

    const onErrorChange = (error: boolean): void => {
        if (error) {
            dispatch(UpdateDialog({ isDisabled: true }));
            setIsTimeInputError(true);
            return;
        }
        dispatch(UpdateDialog({ isDisabled: false }));
        setIsTimeInputError(false);
    };

    return (
        <>
            {isScheduleError && (
                <AlertWrapper>
                    <Alert severity="error" body={t('scheduledtime_error')} />
                </AlertWrapper>
            )}
            <Typography variant="h3">{t('atwhattime')}</Typography>
            <TimePickersWrapper>
                <DayPicker
                    value={inputsDate.format()}
                    minDate={dayjs()}
                    width="300px"
                    displayFormat="DD MMM YYYY"
                    onChange={onChange}
                    readOnly={isDetailsScreen}
                    isIconHidden={isDetailsScreen}
                />
                <TimeInput
                    value={inputsDate.format()}
                    onChange={onChange}
                    onErrorChange={onErrorChange}
                    is12hourFormat={isTime12Format && type !== 'time'}
                    type={type}
                    postfix={dayjs(inputsDate?.toString())?.format?.('z')}
                    readOnly={isDetailsScreen}
                    error={isScheduleError}
                />
            </TimePickersWrapper>
        </>
    );
};
